import * as React from "react"
import { navigate } from "gatsby"
import gql from "graphql-tag"
import { useMutation } from "react-apollo"
import { MdDeleteForever } from "react-icons/md"
import {
  useShowSuccessToast,
  Modal,
  ModalCard,
  StyledModal,
  StyledModalHeader,
  StyledModalBody,
  ThemeCss,
  Button,
  VisuallyHidden,
  Link,
  Spacer,
} from "gatsby-interface"
import { MdInfoOutline } from "react-icons/md"
import { useTriggerErrorAlert } from "@modules/ui/components/ErrorAlert"
import { useTracker } from "@modules/analytics"
import { sites as text } from "@modules/locales/default.js"
import DeleteSiteForm from "./DeleteSiteForm"
import { SitePermissions } from "@modules/site/permissions"
import {
  Subsection,
  SubsectionHeading,
  SubsectionDescription,
} from "./DangerZone.parts"

const DELETE_SITE_MUTATION = gql`
  mutation deleteSite($id: UUID!) {
    deleteSite(id: $id) {
      success
      message
    }
  }
`

export type RemoveSiteProps = {
  siteId: string
  siteName: string
  permissionToDelete?: boolean | null
}

export function RemoveSite({
  siteId,
  siteName,
  permissionToDelete,
}: RemoveSiteProps) {
  const [isDeleteModalOpened, setIsDeleteModalOpened] = React.useState(false)

  return (
    <Subsection>
      <SubsectionHeading>
        {text.deleteSite} <Spacer direction="horizontal" size={3} />
        <Link
          href={`https://gatsby.dev/site-settings-delete-site`}
          title={text.labels.learnAboutDeletingSite}
        >
          <VisuallyHidden>{text.labels.learnAboutDeletingSite}</VisuallyHidden>
          <MdInfoOutline />
        </Link>
      </SubsectionHeading>

      <SitePermissions id={siteId} resource="sites" action="delete">
        <Button
          onClick={() => setIsDeleteModalOpened(true)}
          size="L"
          variant="SECONDARY"
          tone="DANGER"
        >
          {text.deleteSite} <MdDeleteForever />
        </Button>
      </SitePermissions>

      <SubsectionDescription>
        {permissionToDelete
          ? `${text.deleteSiteInfo}`
          : `${text.deleteSiteNoPermission}`}
      </SubsectionDescription>

      {isDeleteModalOpened && (
        <Modal
          isOpen={isDeleteModalOpened}
          aria-label={`Do you want to remove that site?`}
          onDismiss={() => setIsDeleteModalOpened(false)}
          type="error"
        >
          <ModalCard>
            <DeleteSiteModal
              closeModal={() => setIsDeleteModalOpened(false)}
              siteName={siteName}
              siteId={siteId}
            />
          </ModalCard>
        </Modal>
      )}
    </Subsection>
  )
}

type DeleteSiteModalProps = {
  closeModal: () => void
  siteName: string
  siteId: string
}

function DeleteSiteModal({
  closeModal,
  siteName,
  siteId,
}: DeleteSiteModalProps) {
  const [deleteSiteMutation] = useMutation(DELETE_SITE_MUTATION)
  const [setError, errorAlert] = useTriggerErrorAlert()
  const showSuccessToast = useShowSuccessToast()
  const { trackButtonClicked } = useTracker()

  return (
    <StyledModal variant="WARNING">
      <StyledModalHeader onCloseButtonClick={closeModal}>
        {text.deleteSiteConf} <strong css={siteNameCss}>{siteName}?</strong>
      </StyledModalHeader>
      <StyledModalBody>
        {errorAlert}
        <DeleteSiteForm
          submitCallback={() => {
            setError(null)
            trackButtonClicked(text.yesDeleteSite, {
              siteId,
              uiSource: `Site settings`,
            })
            deleteSiteMutation({
              variables: {
                id: siteId,
              },
            })
              .then(({ data }) => {
                if (data.deleteSite.success) {
                  closeModal()
                  showSuccessToast(
                    <React.Fragment>
                      <strong>{siteName} </strong> {text.deletedSuccessfully}
                    </React.Fragment>
                  )
                  return navigate(`/dashboard/sites`)
                }
              })
              .catch(err => setError(err))
          }}
          cancelCallback={closeModal}
          siteName={siteName}
          siteId={siteId}
        />
      </StyledModalBody>
    </StyledModal>
  )
}

/* styles  */

const siteNameCss: ThemeCss = theme => ({
  color: theme.colors.red[60],
})
